import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Users as UsersIcon, Plus, Mail, Key, UserX, UserCheck, Trash2, Search,
  Eye, Filter, AlertTriangle, Lock, Shield, RefreshCw,
} from 'lucide-react';
import api from '../lib/api';

const ROLE_LABEL = {
  admin: 'System Admin',
  hr: 'Human Resources',
  finance: 'Finance / Procurement',
  manager: 'Manager',
  supervisor: 'Supervisor',
  warehouse: 'Warehouse',
  marketing: 'Marketing',
  fleet_manager: 'Fleet Manager',
  auditor: 'Auditor',
};

const ROLE_BADGE = {
  admin: 'bg-red-100 text-red-700',
  hr: 'bg-rose-100 text-rose-700',
  finance: 'bg-purple-100 text-purple-700',
  manager: 'bg-green-100 text-green-700',
  supervisor: 'bg-emerald-100 text-emerald-700',
  warehouse: 'bg-blue-100 text-blue-700',
  marketing: 'bg-pink-100 text-pink-700',
  fleet_manager: 'bg-amber-100 text-amber-700',
  auditor: 'bg-slate-200 text-slate-700',
};

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700',
  deactivated: 'bg-slate-200 text-slate-600',
  invited: 'bg-blue-100 text-blue-700',
  locked: 'bg-amber-100 text-amber-800',
};

const HR_GRANT_LABEL = {
  hr_core: 'HR-Core', payroll: 'Payroll',
  executive_compensation: 'Exec Comp', confidential: 'Confidential',
};

const SCOPE_LABEL = {
  all: 'All', region: 'Region', location: 'Location', team: 'Team', self: 'Self',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Users() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  // Phase 3.12 — Route guard must agree with sidebar visibility (which uses
  // useEffectivePermissions.canModule('users')). Previously this page bounced
  // on a role-string check, so a custom role with users:view (which the engine
  // grants and therefore the sidebar shows) would land here and be kicked
  // back — the "visible then bounced" bug. Now both sources read from the
  // same engine call; the page renders for anyone with users:view and is
  // read-only without users:edit (write controls already use RequireCap).
  const enginePerms = useEffectivePermissions();
  const canViewUsers = enginePerms.can('users', 'view');
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);   // Phase 3.5 admin-defined roles
  const [grantableCaps, setGrantableCaps] = useState([]);  // USER_GRANTABLE_CAPS, from the server
  const [moduleCatalog, setModuleCatalog] = useState([]);  // for the labels
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterScope, setFilterScope] = useState('all');
  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', first_name: '', last_name: '', role: 'manager', password: '' });
  // Access dialog (per-user assignment)
  const [accessUser, setAccessUser] = useState(null);
  const [accessForm, setAccessForm] = useState(null);
  const [savingAccess, setSavingAccess] = useState(false);
  const [confirmExecComp, setConfirmExecComp] = useState(false);
  // Effective-permissions preview
  const [previewUser, setPreviewUser] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  // Delete confirmation
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteActivity, setDeleteActivity] = useState(null);

  useEffect(() => {
    // Wait for perms to resolve before deciding to bounce — otherwise we
    // race against the engine call and kick admins out on a cold load.
    if (me && enginePerms.loaded && !canViewUsers && me.role !== 'admin') {
      toast.error('Access denied. users:view capability required.');
      navigate('/dashboard');
    }
  }, [me, enginePerms.loaded, canViewUsers, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, locsRes, empsRes, customRolesRes, permsRes] = await Promise.all([
        api.get('/users'),
        api.get('/locations').catch(() => ({ data: [] })),
        api.get('/employees?is_active=true').catch(() => ({ data: [] })),
        api.get('/custom-roles').catch(() => ({ data: [] })),
        // Which capabilities may be handed to ONE person, and their labels.
        // Served by the backend from USER_GRANTABLE_CAPS rather than listed
        // here, so this screen can never offer a grant the server refuses.
        api.get('/permissions/engine').catch(() => ({ data: {} })),
      ]);
      setUsers(usersRes.data || []);
      setLocations(locsRes.data || []);
      setEmployees(empsRes.data || []);
      setCustomRoles(customRolesRes.data || []);
      setGrantableCaps(permsRes.data?.user_grantable_caps || []);
      setModuleCatalog(permsRes.data?.modules || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    const status = u.status || (u.is_active === false ? 'deactivated' : 'active');
    if (filterStatus !== 'all' && status !== filterStatus) return false;
    if (filterScope !== 'all' && (u.scope?.type || 'all') !== filterScope) return false;
    if (search) {
      const s = search.toLowerCase();
      const hay = [u.email, u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [users, filterRole, filterStatus, filterScope, search]);

  // Combined role catalog: built-ins + admin-defined custom roles.
  const allRoles = useMemo(() => {
    const builtins = Object.entries(ROLE_LABEL).map(([k, l]) => ({ key: k, label: l, is_custom: false }));
    const customs = customRoles.map(r => ({ key: r.key, label: r.label, is_custom: true }));
    return [...builtins, ...customs];
  }, [customRoles]);

  const labelFor = (roleKey) => {
    const r = allRoles.find(x => x.key === roleKey);
    return r ? r.label : roleKey;
  };

  // ── Create user ──
  const submitCreate = async () => {
    setCreating(true);
    try {
      // No password: 360 accounts sign in with their Microsoft work account.
      const { password: _ignored, ...payload } = createForm;
      await api.post('/auth/register', payload);
      toast.success(`${payload.email} created — they sign in with their Microsoft work account. No password to hand out.`);
      setShowCreate(false);
      setCreateForm({ email: '', first_name: '', last_name: '', role: 'manager', password: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // ── Access dialog ──
  const openAccessDialog = (u) => {
    setAccessUser(u);
    setAccessForm({
      role: u.role,                              // Primary role (editable in dialog).
      add_on_roles: u.add_on_roles || [],
      hr_grants: u.hr_grants || [],
      scope_type: u.scope?.type || 'all',
      scope_value: u.scope?.value || '',
      employee_id: u.employee_id || '',
      // Individual capability grants: {module: [caps]}. A privilege given to
      // ONE person rather than to their role, which is why it is also listed
      // by name on System Status - otherwise it is invisible.
      capability_grants: { ...(u.capability_grants || {}) },
    });
  };

  const toggleAddOnRole = (r) => {
    setAccessForm(f => {
      const cur = new Set(f.add_on_roles);
      cur.has(r) ? cur.delete(r) : cur.add(r);
      return { ...f, add_on_roles: Array.from(cur) };
    });
  };

  const toggleHRGrant = (g) => {
    if (g === 'executive_compensation' && !accessForm.hr_grants.includes(g)) {
      setConfirmExecComp(true);
      return;
    }
    setAccessForm(f => {
      const cur = new Set(f.hr_grants);
      cur.has(g) ? cur.delete(g) : cur.add(g);
      return { ...f, hr_grants: Array.from(cur) };
    });
  };

  const toggleCapGrant = (module, cap) => {
    setAccessForm(f => {
      const grants = { ...(f.capability_grants || {}) };
      const cur = new Set(grants[module] || []);
      cur.has(cap) ? cur.delete(cap) : cur.add(cap);
      // An empty list revokes, and the server treats it that way, so a
      // module with nothing left is dropped rather than sent as [].
      if (cur.size) grants[module] = Array.from(cur); else delete grants[module];
      return { ...f, capability_grants: grants };
    });
  };

  const confirmAndAddExecComp = () => {
    setAccessForm(f => ({ ...f, hr_grants: Array.from(new Set([...f.hr_grants, 'executive_compensation'])) }));
    setConfirmExecComp(false);
  };

  const saveAccess = async () => {
    if (!accessUser) return;
    setSavingAccess(true);
    try {
      // Primary role change first (separate endpoint).
      if (accessForm.role && accessForm.role !== accessUser.role) {
        await api.patch(`/users/${accessUser.id}/role?role=${encodeURIComponent(accessForm.role)}`);
      }
      await api.put(`/users/${accessUser.id}/access`, {
        add_on_roles: accessForm.add_on_roles,
        hr_grants: accessForm.hr_grants,
        scope_type: accessForm.scope_type,
        scope_value: accessForm.scope_value || null,
        capability_grants: accessForm.capability_grants || {},
      });
      // Also update employee_id via profile endpoint if changed
      if ((accessForm.employee_id || '') !== (accessUser.employee_id || '')) {
        await api.put(`/users/${accessUser.id}`, { employee_id: accessForm.employee_id || null });
      }
      toast.success('Access updated');
      setAccessUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update access');
    } finally {
      setSavingAccess(false);
    }
  };

  // ── Effective-permissions preview ──
  const openPreview = async (u) => {
    setPreviewUser(u);
    setPreviewData(null);
    try {
      const res = await api.get(`/users/${u.id}/effective-permissions`);
      setPreviewData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    }
  };

  // ── Deactivate / Reactivate ──
  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? Their login will be revoked but all history is preserved.`)) return;
    try {
      await api.patch(`/users/${u.id}/deactivate`);
      toast.success('User deactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate');
    }
  };
  const reactivate = async (u) => {
    try {
      await api.patch(`/users/${u.id}/reactivate`);
      toast.success('User reactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate');
    }
  };

  // ── Reset password — sends link, never typed by admin ──
  const sendReset = async (u) => {
    if (u.sso_only !== false && !u.sso_exempt) {
      toast.info(`${u.email} signs in with Microsoft. Reset their password in Microsoft 365 / Entra ID — TriCorps360 does not hold one.`);
      return;
    }
    try {
      await api.post(`/auth/request-password-reset`, { email: u.email });
      toast.success(`Reset link sent to ${u.email}`);
    } catch (e) {
      // Endpoint may not exist; fall back to admin-issued temporary password.
      try {
        const tmp = generatePassword();
        await api.post(`/users/${u.id}/reset-password`, { new_password: tmp });
        toast.success(`Temporary password issued: ${tmp} (user must change at next login)`);
      } catch (e2) {
        toast.error(e2.response?.data?.detail || 'Failed to issue reset');
      }
    }
  };

  // ── Hard delete — admin only, must have zero activity ──
  const openDelete = async (u) => {
    setDeleteUser(u);
    setDeleteActivity(null);
    try {
      const res = await api.get(`/users/${u.id}/activity-summary`);
      setDeleteActivity(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load activity');
      setDeleteUser(null);
    }
  };
  const confirmHardDelete = async () => {
    if (!deleteUser) return;
    try {
      await api.delete(`/users/${deleteUser.id}`);
      toast.success('User permanently deleted');
      setDeleteUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500" data-testid="users-loading">Loading users…</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-200">
            <UsersIcon className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">User Management</h1>
            <p className="text-sm text-slate-600">Roles, grants, scope, and account lifecycle</p>
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} className="min-h-[44px]" data-testid="open-create-user-btn">
          <Plus className="w-4 h-4 mr-2" /> New user
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)}
                   className="pl-9 min-h-[44px]" data-testid="users-search" />
          </div>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="min-h-[44px]" data-testid="filter-role"><SelectValue placeholder="All roles" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {allRoles.map(r => <SelectItem key={r.key} value={r.key}>{r.label}{r.is_custom ? ' (custom)' : ''}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="min-h-[44px]" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
              <SelectItem value="invited">Invited</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterScope} onValueChange={setFilterScope}>
            <SelectTrigger className="min-h-[44px]" data-testid="filter-scope"><SelectValue placeholder="All scopes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scopes</SelectItem>
              {Object.entries(SCOPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* User list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{filtered.length} of {users.length} users</CardTitle>
        </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-2">User</th>
                  <th className="px-2 py-2">Role</th>
                  <th className="px-2 py-2 hidden md:table-cell">Add-on</th>
                  <th className="px-2 py-2 hidden lg:table-cell">Scope</th>
                  <th className="px-2 py-2 hidden lg:table-cell">Linked Employee</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2 hidden md:table-cell">Last login</th>
                  <th className="px-2 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => {
                  const status = u.status || (u.is_active === false ? 'deactivated' : 'active');
                  const linkedEmp = u.employee_id ? employees.find(e => e.id === u.employee_id) : null;
                  return (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{u.first_name} {u.last_name}</div>
                        <div className="text-xs text-slate-500 break-all">{u.email}</div>
                        <Badge variant="outline" className="mt-1 text-[10px] font-normal"
                               data-testid={`signin-method-${u.id}`}>
                          {u.sso_only === false || u.sso_exempt ? 'Local password' : 'Microsoft sign-in'}
                        </Badge>
                      </td>
                      <td className="px-2 py-3">
                        <Badge className={`${ROLE_BADGE[u.role] || 'bg-slate-100 text-slate-700'} whitespace-nowrap`}>
                          {labelFor(u.role)}
                        </Badge>
                      </td>
                      <td className="px-2 py-3 hidden md:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(u.add_on_roles || []).map(r => (
                            <Badge key={r} variant="outline" className="text-xs">{ROLE_LABEL[r] || r}</Badge>
                          ))}
                          {(!u.add_on_roles || u.add_on_roles.length === 0) && <span className="text-xs text-slate-400">—</span>}
                        </div>
                      </td>
                      <td className="px-2 py-3 hidden lg:table-cell">
                        <Badge variant="outline" className="text-xs">
                          {SCOPE_LABEL[u.scope?.type || 'all']}{u.scope?.value ? `: ${u.scope.value}` : ''}
                        </Badge>
                      </td>
                      <td className="px-2 py-3 hidden lg:table-cell text-xs text-slate-700">
                        {linkedEmp ? `${linkedEmp.first_name} ${linkedEmp.last_name}` : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-2 py-3">
                        <Badge className={STATUS_BADGE[status] || 'bg-slate-100'}>{status}</Badge>
                      </td>
                      <td className="px-2 py-3 hidden md:table-cell text-xs text-slate-600">{fmtDate(u.last_login)}</td>
                      <td className="px-2 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openPreview(u)}
                                  className="min-h-[36px] px-2" data-testid={`preview-${u.id}`} title="Effective permissions">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openAccessDialog(u)}
                                  className="min-h-[36px] px-2" data-testid={`access-${u.id}`} title="Edit access">
                            <Shield className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => sendReset(u)}
                                  className="min-h-[36px] px-2" data-testid={`reset-${u.id}`}
                                  title={u.sso_only === false || u.sso_exempt ? 'Reset password' : 'Password managed by Microsoft'}>
                            <Key className={`w-4 h-4 ${u.sso_only === false || u.sso_exempt ? '' : 'text-slate-400'}`} />
                          </Button>
                          {status !== 'deactivated' && (
                            <Button variant="ghost" size="sm" onClick={() => deactivate(u)}
                                    className="min-h-[36px] px-2 text-amber-700" data-testid={`deactivate-${u.id}`} title="Deactivate">
                              <UserX className="w-4 h-4" />
                            </Button>
                          )}
                          {status === 'deactivated' && (
                            <Button variant="ghost" size="sm" onClick={() => reactivate(u)}
                                    className="min-h-[36px] px-2 text-emerald-700" data-testid={`reactivate-${u.id}`} title="Reactivate">
                              <UserCheck className="w-4 h-4" />
                            </Button>
                          )}
                          {me?.role === 'admin' && (
                            <Button variant="ghost" size="sm" onClick={() => openDelete(u)}
                                    className="min-h-[36px] px-2 text-rose-700" data-testid={`delete-${u.id}`} title="Hard delete (admin only)">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-500">No users match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Create user</DialogTitle>
            <DialogDescription>
              The user signs in with their Microsoft work account — no password is created here,
              and nothing has to be changed at first login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First name</Label><Input value={createForm.first_name} onChange={e => setCreateForm(f => ({ ...f, first_name: e.target.value }))} data-testid="create-first-name" /></div>
              <div><Label>Last name</Label><Input value={createForm.last_name} onChange={e => setCreateForm(f => ({ ...f, last_name: e.target.value }))} data-testid="create-last-name" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} data-testid="create-email" /></div>
            <div>
              <Label>Primary role</Label>
              <Select value={createForm.role} onValueChange={v => setCreateForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allRoles.filter(r => r.key !== 'admin').map(r => (
                    <SelectItem key={r.key} value={r.key}>{r.label}{r.is_custom ? ' (custom)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-slate-500">
              You'll set HR grants, add-on roles, and scope on the next screen after creation.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating || !createForm.email || !createForm.first_name || !createForm.last_name}
                    data-testid="submit-create-user">
              {creating ? 'Creating…' : 'Create user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Dialog */}
      {accessUser && accessForm && (
        <Dialog open={!!accessUser} onOpenChange={(o) => !o && setAccessUser(null)}>
          <DialogContent className="max-w-2xl" data-testid="access-dialog">
            <DialogHeader>
              <DialogTitle>Access for {accessUser.first_name} {accessUser.last_name}</DialogTitle>
              <DialogDescription>Add-on roles, HR sub-area grants, and record scope.</DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              {/* Primary role */}
              <div>
                <Label className="mb-1.5 block">Primary role</Label>
                <Select
                  value={accessForm.role}
                  onValueChange={v => setAccessForm(f => ({
                    ...f,
                    role: v,
                    // If the new primary role is currently an add-on, drop it from add-ons (can't be both).
                    add_on_roles: (f.add_on_roles || []).filter(r => r !== v),
                  }))}
                >
                  <SelectTrigger data-testid="primary-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allRoles.filter(r => r.key !== 'admin' || accessUser.role === 'admin').map(r => (
                      <SelectItem key={r.key} value={r.key}>{r.label}{r.is_custom ? ' (custom)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {accessForm.role !== accessUser.role && (
                  <div className="text-[11px] text-amber-700 mt-1">
                    Primary role will change from <strong>{labelFor(accessUser.role)}</strong> to <strong>{labelFor(accessForm.role)}</strong> on save.
                  </div>
                )}
              </div>

              {/* Add-on roles */}
              <div>
                <Label className="mb-1.5 block">Add-on roles</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(ROLE_LABEL).filter(([k]) => k !== 'admin' && k !== accessForm.role).map(([k, l]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => toggleAddOnRole(k)}
                      data-testid={`addon-${k}`}
                      className={`text-xs px-2.5 py-1.5 rounded border min-h-[36px] ${
                        accessForm.add_on_roles.includes(k) ? 'bg-blue-600 text-blue-50 border-blue-700' : 'bg-surface text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >{l}</button>
                  ))}
                </div>
              </div>

              {/* HR grants */}
              <div>
                <Label className="mb-1.5 block">HR sub-area grants</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(HR_GRANT_LABEL).map(([k, l]) => {
                    const isExec = k === 'executive_compensation';
                    const on = accessForm.hr_grants.includes(k);
                    return (
                      <label key={k} className={`flex items-start justify-between gap-3 p-2.5 rounded border ${
                        on ? 'bg-blue-50 border-blue-200' : 'bg-surface border-slate-200'
                      } ${isExec ? 'ring-1 ring-rose-200' : ''}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium flex items-center gap-1.5">
                            {l}
                            {isExec && <Lock className="w-3 h-3 text-rose-600" />}
                          </div>
                          <div className="text-xs text-slate-600">
                            {k === 'hr_core' && 'Employee Core records'}
                            {k === 'payroll' && 'Banking, comp, withholdings, client bill rates'}
                            {k === 'executive_compensation' && 'Highest sensitivity'}
                            {k === 'confidential' && 'SSN, EEO, I-9, WOTC'}
                          </div>
                        </div>
                        <Switch checked={on} onCheckedChange={() => toggleHRGrant(k)} data-testid={`grant-${k}`} />
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Individual capability grants (2026-09-30). A privilege given
                  to ONE person rather than to their role. The list comes from
                  the server's USER_GRANTABLE_CAPS, so this screen cannot offer
                  something the server would refuse, and every grant here is
                  named on System Status - a privilege nobody can see is a
                  privilege nobody reviews. */}
              {grantableCaps.length > 0 && (
                <div>
                  <Label className="mb-1.5 block">Individual capability grants</Label>
                  <p className="text-xs text-slate-600 mb-2">
                    Given to this person only, on top of their role. Listed by name on
                    System Status, and every grant and revoke is in the audit log.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {grantableCaps.map(({ module, capabilities }) => {
                      const label = moduleCatalog.find(m => m.key === module)?.label
                        || module.replace(/_/g, ' ');
                      return capabilities.map((cap) => {
                        const on = (accessForm.capability_grants?.[module] || []).includes(cap);
                        return (
                          <label key={`${module}:${cap}`}
                            className={`flex items-start justify-between gap-3 p-2.5 rounded border ${
                              on ? 'bg-blue-50 border-blue-200' : 'bg-surface border-slate-200'}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-medium">{label}</div>
                              <div className="text-xs text-slate-600">
                                {module === 'scheduling_grid'
                                  ? 'Opens the weekly coverage grid. Still needs Scheduling: Edit to change a shift.'
                                  : module === 'bill_rates'
                                    ? 'What the client is charged, on a shift row and on rate lines.'
                                    : module === 'scheduling_pay_rates'
                                      ? "The officer's own pay rate on a shift row."
                                      : `${cap} only`}
                              </div>
                            </div>
                            <Switch checked={on} onCheckedChange={() => toggleCapGrant(module, cap)}
                              data-testid={`cap-grant-${module}-${cap}`} />
                          </label>
                        );
                      });
                    })}
                  </div>
                </div>
              )}

              {/* Scope */}
              <div>
                <Label className="mb-1.5 block">Record scope</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Select value={accessForm.scope_type} onValueChange={v => setAccessForm(f => ({ ...f, scope_type: v, scope_value: '' }))}>
                    <SelectTrigger data-testid="scope-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(SCOPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {accessForm.scope_type === 'location' && (
                    <Select value={accessForm.scope_value} onValueChange={v => setAccessForm(f => ({ ...f, scope_value: v }))}>
                      <SelectTrigger data-testid="scope-value-location"><SelectValue placeholder="Pick location" /></SelectTrigger>
                      <SelectContent>
                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {accessForm.scope_type === 'region' && (
                    <Input placeholder="State (e.g. OK)" value={accessForm.scope_value}
                           onChange={e => setAccessForm(f => ({ ...f, scope_value: e.target.value.toUpperCase() }))}
                           maxLength={2} data-testid="scope-value-region" />
                  )}
                </div>
              </div>

              {/* Linked employee */}
              <div>
                <Label className="mb-1.5 block">Linked employee record (optional)</Label>
                <Select value={accessForm.employee_id || 'none'} onValueChange={v => setAccessForm(f => ({ ...f, employee_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="linked-employee"><SelectValue placeholder="No link" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="none">— None —</SelectItem>
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_number || e.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAccessUser(null)}>Cancel</Button>
              <Button onClick={saveAccess} disabled={savingAccess} data-testid="save-access-btn">
                {savingAccess ? 'Saving…' : 'Save access'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Exec Comp confirmation */}
      <Dialog open={confirmExecComp} onOpenChange={setConfirmExecComp}>
        <DialogContent data-testid="exec-comp-grant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Grant Executive Compensation access
            </DialogTitle>
            <DialogDescription>
              Highest-sensitivity grant. This audits a <code>GRANT_EXEC_COMP</code> row visible in the audit log.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmExecComp(false)}>Cancel</Button>
            <Button onClick={confirmAndAddExecComp} className="bg-rose-600 hover:bg-rose-700 text-rose-50" data-testid="exec-comp-confirm">
              I understand — grant access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Effective-permissions preview */}
      {previewUser && (
        <Dialog open={!!previewUser} onOpenChange={(o) => !o && setPreviewUser(null)}>
          <DialogContent className="max-w-3xl" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle>Effective permissions — {previewUser.first_name} {previewUser.last_name}</DialogTitle>
              <DialogDescription>Resolved by the access engine, exactly as the API enforces.</DialogDescription>
            </DialogHeader>
            {!previewData && <div className="text-slate-500">Loading…</div>}
            {previewData && (
              <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><div className="text-xs text-slate-500 uppercase">Role</div><div className="font-semibold">{previewData.role}</div></div>
                  <div><div className="text-xs text-slate-500 uppercase">Scope</div><div className="font-semibold">{previewData.scope?.type}{previewData.scope?.value ? `: ${previewData.scope.value}` : ''}</div></div>
                  <div><div className="text-xs text-slate-500 uppercase">Add-on roles</div><div className="font-semibold">{(previewData.add_on_roles || []).join(', ') || '—'}</div></div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-1">Accessible tiers</div>
                  <div className="flex gap-1 flex-wrap">
                    {(previewData.accessible_tiers || []).map(t => <Badge key={t} variant="outline">{t}</Badge>)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-1">HR grants</div>
                  <div className="flex gap-1 flex-wrap">
                    {(previewData.hr_grants || []).length === 0 && <span className="text-slate-400">none</span>}
                    {(previewData.hr_grants || []).map(g => <Badge key={g} className={g === 'executive_compensation' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-700'}>{HR_GRANT_LABEL[g] || g}</Badge>)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase mb-1">Module capabilities</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                    {Object.entries(previewData.capabilities || {}).map(([m, caps]) => (
                      <div key={m} className="flex justify-between gap-2 p-1.5 border rounded">
                        <span className="font-medium">{m}</span>
                        <span className="text-slate-600">{(caps || []).join(', ') || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setPreviewUser(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Hard Delete confirmation */}
      {deleteUser && (
        <Dialog open={!!deleteUser} onOpenChange={(o) => !o && setDeleteUser(null)}>
          <DialogContent data-testid="hard-delete-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-rose-700">
                <Trash2 className="w-5 h-5" /> Hard delete {deleteUser.email}
              </DialogTitle>
              <DialogDescription>
                Permanent. Blocked when the user has any historical activity.
              </DialogDescription>
            </DialogHeader>
            {!deleteActivity && <div className="text-slate-500">Checking activity…</div>}
            {deleteActivity && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(deleteActivity).filter(([k]) => !k.startsWith('_')).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2 p-1.5 border rounded">
                      <span className="text-slate-700">{k.replace(/_/g, ' ')}</span>
                      <span className={v > 0 ? 'font-semibold text-rose-700' : 'text-slate-500'}>{v}</span>
                    </div>
                  ))}
                </div>
                {(deleteActivity._total || 0) > 0 && (
                  <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
                    <AlertTriangle className="w-4 h-4 inline mr-1.5" />
                    This user has historical activity. Use <strong>Deactivate</strong> instead to preserve the audit trail.
                  </div>
                )}
                {(deleteActivity._total || 0) === 0 && (
                  <div className="text-sm bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-900">
                    No historical activity. This user can be safely hard-deleted.
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteUser(null)}>Cancel</Button>
              <Button onClick={confirmHardDelete}
                      disabled={!deleteActivity || (deleteActivity._total || 0) > 0}
                      className="bg-rose-600 hover:bg-rose-700 text-rose-50"
                      data-testid="confirm-hard-delete">
                Permanently delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function generatePassword() {
  // Must satisfy the server policy: 12+ chars with upper, lower and a digit.
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const pick = (set) => set[Math.floor(Math.random() * set.length)];
  const all = upper + lower + digits;
  const chars = [pick(upper), pick(lower), pick(digits)];
  for (let i = 0; i < 11; i++) chars.push(pick(all));
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('') + '!';
}
